import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, fmtDate, normalizeLocalizedRequired, relTime, scopeQuery, useScope, type Localized } from "../api";
import { useT } from "../i18n";
import { ConfirmDelete, Field, LocalizedField, Msg, useFlash } from "../ui";
import { NotificationActionMatrix } from "./NotificationActionMatrix";
import { NotificationOthers } from "./NotificationOthers";

export const NOTIFICATION_TABS = ["templates", "notificationMatrix", "others"] as const;
export type NotificationsTab = (typeof NOTIFICATION_TABS)[number];

const TAB_LABEL_KEY: Record<NotificationsTab, string> = {
  templates: "admin.notifications.tabTemplates",
  notificationMatrix: "admin.notifications.tabMatrix",
  others: "admin.notifications.tabOthers",
};

/**
 * Notifications (FR-ADM-01..03): send form (audience chips with counters or one fixed recipient,
 * template list on the right, push preview, test to me, schedule), template editor, sent log.
 */

interface Template {
  id: string;
  slug: string;
  name: string;
  dimension: string | null;
  category: string;
  audienceDefault: string;
  title: Localized;
  body: Localized;
  buttons: Array<{ label: Localized; route: string }>;
  isCustom: boolean;
  isSystem: boolean;
}
interface TemplatesData {
  templates: Template[];
  audiences: string[];
  categories: string[];
}
interface SendsData {
  today: { sent: number; queued: number; skipped: number; opened: number };
  sends: Array<{ id: string; audience: string; recipientName: string | null; title: string; body: string; channels: string; recipients: number; scheduledFor: string | null; sentAt: string | null; createdAt: string; sentBy: string | null; template: string | null }>;
}

export function AdminNotifications({ tab }: { tab: NotificationsTab }) {
  const { t, locale } = useT();
  const navigate = useNavigate();
  const scope = useScope();
  const qc = useQueryClient();
  const activeTab = NOTIFICATION_TABS.includes(tab) ? tab : "notificationMatrix";
  const [sending, setSending] = useState<{ template?: Template } | null>(null);
  const [editing, setEditing] = useState<Template | "new" | null>(null);
  const [filter, setFilter] = useState("");
  const sq = scopeQuery(scope, "church");
  const tq = useQuery({ queryKey: ["admin", "templates", scope.churchId], queryFn: () => api<TemplatesData>(`/admin/notifications/templates${sq}`) });
  const lq = useQuery({ queryKey: ["admin", "sends", scope.churchId], queryFn: () => api<SendsData>(`/admin/notifications/sends${sq}`), refetchInterval: 60_000 });
  const templates = useMemo(() => (tq.data?.templates ?? []).filter((x) => !filter || `${x.name} ${x.dimension ?? ""}`.toLowerCase().includes(filter.toLowerCase())), [tq.data, filter]);

  return (
    <>
      <h1>{t("admin.notifications.title")}</h1>
      <p className="muted">{t("admin.notifications.body")}</p>

      <div className="tabs" role="tablist" aria-label={t("admin.notifications.title")}>
        {NOTIFICATION_TABS.map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={k === activeTab}
            className={k === activeTab ? "active" : ""}
            onClick={() => navigate({ to: "/admin/notifications", search: (prev) => ({ ...prev, tab: k }) })}
          >
            {t(TAB_LABEL_KEY[k])}
          </button>
        ))}
      </div>

      {activeTab === "notificationMatrix" ? <NotificationActionMatrix /> : null}
      {activeTab === "others" ? <NotificationOthers /> : null}
      {activeTab === "templates" ? (
        <>
      <div className="toolbar">
        <button className="btn" onClick={() => setSending({})}>
          {t("admin.notifications.send")}
        </button>
        <button className="btn ghost" onClick={() => setEditing("new")}>
          {t("admin.notifications.newTemplate")}
        </button>
        {lq.data ? <span className="muted small">{t("admin.notifications.today", lq.data.today)}</span> : null}
      </div>

      <div className="two-col wide">
        <div className="panel">
          <div className="toolbar">
            <h2>{t("admin.notifications.templates")}</h2>
            <input placeholder={t("admin.notifications.filter")} value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
          <div className="list">
            {templates.map((x) => (
              <div key={x.id} className="listrow">
                <div className="grow">
                  <strong>{x.name}</strong> <span className="pill">{x.isCustom ? t("admin.notifications.custom") : t("admin.notifications.system")}</span>
                  {x.dimension ? <span className="pill">{x.dimension}</span> : null}
                  <div className="muted small">{x.title[locale] ?? x.title.de}</div>
                </div>
                <button className="btn small ghost" onClick={() => setEditing(x)}>
                  {t("admin.common.edit")}
                </button>
                <button className="btn small" onClick={() => setSending({ template: x })}>
                  {t("admin.notifications.sendWith")}
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="panel">
          <h2>{t("admin.notifications.log")}</h2>
          {lq.data?.sends.length === 0 ? <p className="muted">{t("admin.notifications.logEmpty")}</p> : null}
          <div className="list">
            {lq.data?.sends.map((s) => (
              <div key={s.id} className="listrow">
                <div className="grow">
                  <strong>{s.title}</strong>
                  <div className="muted small">{s.body}</div>
                  <div className="muted small">
                    {s.audience === "user" ? t("admin.notifications.to", { name: s.recipientName ?? "" }) : t(`admin.notifications.aud.${s.audience}`)} · {t(`admin.notifications.via.${s.channels === "email" || s.channels === "both" ? s.channels : "push"}`)} · {s.sentAt ? `${t("admin.notifications.recipients", { n: s.recipients })} · ${relTime(s.sentAt, locale)}` : s.scheduledFor ? t("admin.notifications.scheduledFor", { date: fmtDate(s.scheduledFor, locale, { dateStyle: "medium", timeStyle: "short" }) }) : ""}
                    {s.sentBy ? ` · ${t("admin.notifications.by", { name: s.sentBy })}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
        </>
      ) : null}

      {sending ? <SendDialog template={sending.template} templates={tq.data?.templates ?? []} onClose={() => setSending(null)} onSent={() => void qc.invalidateQueries({ queryKey: ["admin", "sends"] })} /> : null}
      {editing ? <TemplateEditor key={editing === "new" ? "new" : editing.id} template={editing === "new" ? null : editing} categories={tq.data?.categories ?? []} audiences={tq.data?.audiences ?? []} onClose={() => setEditing(null)} onSaved={() => void qc.invalidateQueries({ queryKey: ["admin", "templates"] }).then(() => setEditing(null))} /> : null}
    </>
  );
}

/** Send form, also used from every people list with a fixed recipient (FR-TEAM-05). */
export function SendDialog({ recipient, template, templates, onClose, onSent }: { recipient?: { userId: string; name: string }; template?: Template; templates?: Template[]; onClose: () => void; onSent?: () => void }) {
  const { t, locale } = useT();
  const scope = useScope();
  const sq = scopeQuery(scope, "church");
  const [flash, run, busy] = useFlash();
  const [title, setTitle] = useState(template ? (template.title[locale] ?? template.title.de ?? "") : "");
  const [body, setBody] = useState(template ? (template.body[locale] ?? template.body.de ?? "") : "");
  const [audience, setAudience] = useState(template?.audienceDefault ?? "all");
  const [medium, setMedium] = useState<"push" | "email" | "both">("push");
  const [reach, setReach] = useState<"church" | "everyone" | "person">(recipient ? "person" : "church");
  const [personQuery, setPersonQuery] = useState("");
  const [person, setPerson] = useState<{ userId: string; name: string } | null>(recipient ?? null);
  const [targetChurch, setTargetChurch] = useState(scope.churchId ?? "");
  const [tpl, setTpl] = useState<Template | undefined>(template);
  const [filter, setFilter] = useState("");
  const [schedule, setSchedule] = useState("");
  const countScope = reach === "everyone" ? { ...scope, churchId: null } : !scope.churchId && targetChurch ? { ...scope, churchId: targetChurch } : scope;
  const aq = useQuery({ queryKey: ["admin", "audiences", countScope.churchId, reach], queryFn: () => api<{ counts: Record<string, number> }>(`/admin/notifications/audiences${scopeQuery(countScope, "church")}`), enabled: !recipient && reach !== "person" });
  const rq = useQuery({
    queryKey: ["admin", "recipients", scope.churchId, personQuery],
    queryFn: () => api<{ people: Array<{ userId: string; name: string }> }>(`/admin/notifications/recipients${scopeQuery(scope, "church", { q: personQuery.trim() })}`),
    enabled: reach === "person" && !recipient && personQuery.trim().length >= 2,
  });
  const tq = useQuery({ queryKey: ["admin", "templates", scope.churchId], queryFn: () => api<TemplatesData>(`/admin/notifications/templates${sq}`), enabled: !templates });
  const list = (templates ?? tq.data?.templates ?? []).filter((x) => !filter || `${x.name} ${x.dimension ?? ""}`.toLowerCase().includes(filter.toLowerCase()));
  const adopt = (x: Template) => {
    setTpl(x);
    setTitle(x.title[locale] ?? x.title.de ?? "");
    setBody(x.body[locale] ?? x.body.de ?? "");
    setAudience(x.audienceDefault);
  };
  const count = recipient || reach === "person" ? 1 : (aq.data?.counts[audience] ?? 0);
  const needsChurch = scope.canPickScope && reach === "church" && !scope.churchId && !targetChurch;
  const needsPerson = !recipient && reach === "person" && !person;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const send = async () => {
    const channels = medium === "both" ? ["push", "email"] : [medium];
    const ok = await run(
      () => api(`/admin/notifications/send${sq}`, { method: "POST", json: { title: title.trim(), body: body.trim(), channels, reach: recipient ? "person" : reach, audience: recipient || reach === "person" ? undefined : audience, userId: recipient?.userId ?? person?.userId, churchId: scope.canPickScope && reach === "church" && !scope.churchId ? targetChurch : undefined, templateId: tpl?.id ?? null, scheduledFor: schedule ? new Date(schedule).toISOString() : null } }),
      schedule ? t("admin.notifications.scheduled") : t("admin.notifications.sent", { n: count }),
    );
    if (ok) {
      onSent?.();
      setTimeout(onClose, 900);
    }
  };
  const test = () => run(async () => {
    const r = await api<{ status: string }>(`/admin/notifications/test${sq}`, { method: "POST", json: { title: title.trim(), body: body.trim(), category: tpl?.category, channels: medium === "both" ? ["push", "email"] : [medium] } });
    return r;
  }, t("admin.notifications.testSent", { status: "queued" }));

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="toolbar">
          <h2>{t("admin.notifications.send")}</h2>
          <button className="btn small ghost" onClick={onClose} style={{ marginInlineStart: "auto" }}>
            {t("admin.notifications.close")}
          </button>
        </div>
        <div className="two-col wide">
          <div className="form">
            <Field label={t("admin.notifications.medium")}>
              <div className="chips">
                {(["push", "email", "both"] as const).map((m) => (
                  <button key={m} type="button" className={`chip ${medium === m ? "on" : ""}`} onClick={() => setMedium(m)}>
                    {t(`admin.notifications.via.${m}`)}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={t("admin.notifications.audience")}>
              {recipient ? (
                <div className="chips">
                  <span className="chip on">{t("admin.notifications.to", { name: recipient.name })}</span>
                </div>
              ) : (
                <>
                  <div className="chips">
                    <button type="button" className={`chip ${reach === "church" ? "on" : ""}`} onClick={() => setReach("church")}>
                      {t("admin.notifications.reachChurch")}
                    </button>
                    {scope.canPickScope ? (
                      <button type="button" className={`chip ${reach === "everyone" ? "on" : ""}`} onClick={() => setReach("everyone")}>
                        {t("admin.notifications.reachEveryone")}
                      </button>
                    ) : null}
                    <button type="button" className={`chip ${reach === "person" ? "on" : ""}`} onClick={() => setReach("person")}>
                      {t("admin.notifications.reachPerson")}
                    </button>
                  </div>
                  {reach === "church" && scope.canPickScope && !scope.churchId ? (
                    <select value={targetChurch} onChange={(e) => setTargetChurch(e.target.value)}>
                      <option value="">{t("admin.notifications.pickChurch")}</option>
                      {scope.churches.map((ch) => (
                        <option key={ch.id} value={ch.id}>
                          {ch.name}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  {reach !== "person" ? (
                    <div className="chips">
                      {["all", "guest", "global", "hey_church", "leader", "pastor_admin"].map((a) => (
                        <button key={a} type="button" className={`chip ${audience === a ? "on" : ""}`} onClick={() => setAudience(a)}>
                          {t(`admin.notifications.aud.${a}`)} <span className="muted">{aq.data?.counts[a] ?? "–"}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <>
                      <input placeholder={t("admin.notifications.personSearch")} value={personQuery} onChange={(e) => { setPersonQuery(e.target.value); setPerson(null); }} />
                      {person ? <p className="muted small">{t("admin.notifications.to", { name: person.name })}</p> : null}
                      <div className="list compact">
                        {rq.data?.people.map((p) => (
                          <button key={p.userId} type="button" className={`listrow clickable ${person?.userId === p.userId ? "on" : ""}`} onClick={() => setPerson(p)}>
                            <strong>{p.name}</strong>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </Field>
            {tpl ? <p className="muted small">{t("admin.notifications.usingTemplate", { name: tpl.name })}</p> : null}
            <Field label={t("admin.notifications.titleLabel")}>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
            </Field>
            <Field label={t("admin.notifications.textLabel")} hint={t("admin.notifications.placeholders")}>
              <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={1000} />
            </Field>
            <Field label={t("admin.notifications.schedule")}>
              <input type="datetime-local" value={schedule} onChange={(e) => setSchedule(e.target.value)} />
            </Field>
            <div className="push-preview">
              <div className="eyebrow">{t(`admin.notifications.preview.${medium}`)}</div>
              <div className="push">
                <strong>{title || "…"}</strong>
                <div>{body || "…"}</div>
              </div>
            </div>
            <div className="actions">
              <button className="btn" disabled={busy || !title.trim() || !body.trim() || needsChurch || needsPerson} onClick={() => void send()}>
                {recipient ? t("admin.notifications.sendOne") : t("admin.notifications.sendTo", { n: count })}
              </button>
              <button className="btn ghost" disabled={busy || !title.trim() || !body.trim()} onClick={() => void test()}>
                {t("admin.notifications.testToMe")}
              </button>
              <Msg msg={flash} />
            </div>
          </div>
          <div className="panel">
            <input placeholder={t("admin.notifications.filter")} value={filter} onChange={(e) => setFilter(e.target.value)} />
            <div className="list compact">
              {list.map((x) => (
                <button key={x.id} type="button" className={`listrow clickable ${tpl?.id === x.id ? "on" : ""}`} onClick={() => adopt(x)}>
                  <div className="grow">
                    <strong>{x.name}</strong> {x.dimension ? <span className="pill">{x.dimension}</span> : null}
                    <div className="muted small">{x.title[locale] ?? x.title.de}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({ template, categories, audiences, onClose, onSaved }: { template: Template | null; categories: string[]; audiences: string[]; onClose: () => void; onSaved: () => void }) {
  const { t } = useT();
  const scope = useScope();
  const sq = scopeQuery(scope, "church");
  const [flash, run, busy] = useFlash();
  const [f, setF] = useState({
    name: template?.name ?? "",
    dimension: template?.dimension ?? "",
    category: template?.category ?? "personal",
    audienceDefault: template?.audienceDefault ?? "all",
    title: template?.title ?? ({} as Localized),
    body: template?.body ?? ({} as Localized),
    buttons: template?.buttons ?? [],
  });
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const normTitle = normalizeLocalizedRequired(f.title);
    const normBody = normalizeLocalizedRequired(f.body);
    if (!normTitle || !normBody) {
      await run(async () => {
        throw new Error(t("admin.notifications.localizedRequired"));
      });
      return;
    }
    const body = {
      name: f.name.trim(),
      dimension: f.dimension.trim() || null,
      category: f.category,
      audienceDefault: f.audienceDefault,
      title: normTitle,
      body: normBody,
      buttons: f.buttons.filter((b) => b.route.trim()),
    };
    const ok = await run(() => (template ? api(`/admin/notifications/templates/${template.id}${sq}`, { method: "PATCH", json: body }) : api(`/admin/notifications/templates${sq}`, { method: "POST", json: body })), t("admin.common.saved"));
    if (ok) onSaved();
  };
  const remove = async () => {
    if (!template) return;
    if (await run(() => api(`/admin/notifications/templates/${template.id}${sq}`, { method: "DELETE" }))) onSaved();
  };
  return (
    <form className="form panel editor" onSubmit={save}>
      <h2>{template ? `${t("admin.common.edit")} · ${template.name}` : t("admin.notifications.newTemplate")}</h2>
      <div className="two-col">
        <Field label={t("admin.notifications.name")}>
          <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={t("admin.notifications.dimension")}>
          <input value={f.dimension} onChange={(e) => setF({ ...f, dimension: e.target.value })} />
        </Field>
        <Field label={t("admin.notifications.category")}>
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
            {categories.map((c) => (
              <option key={c} value={c}>
                {t(`admin.notifications.cat.${c}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("admin.notifications.audienceDefault")}>
          <select value={f.audienceDefault} onChange={(e) => setF({ ...f, audienceDefault: e.target.value })}>
            {audiences.map((a) => (
              <option key={a} value={a}>
                {t(`admin.notifications.aud.${a}`)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <LocalizedField label={t("admin.notifications.titleLabel")} value={f.title} onChange={(v) => setF({ ...f, title: v })} required />
      <LocalizedField label={t("admin.notifications.textLabel")} value={f.body} onChange={(v) => setF({ ...f, body: v })} multiline required />
      <p className="muted small">{t("admin.notifications.placeholders")}</p>
      <div className="field">
        <span className="lbl">{t("admin.notifications.buttons")}</span>
        {f.buttons.map((b, i) => (
          <div key={i} className="pair">
            <input placeholder={t("admin.notifications.buttonLabel")} value={b.label.de ?? ""} onChange={(e) => setF({ ...f, buttons: f.buttons.map((x, j) => (j === i ? { ...x, label: { ...x.label, de: e.target.value, en: x.label.en ?? e.target.value } } : x)) })} />
            <input placeholder={t("admin.notifications.buttonRoute")} value={b.route} onChange={(e) => setF({ ...f, buttons: f.buttons.map((x, j) => (j === i ? { ...x, route: e.target.value } : x)) })} />
          </div>
        ))}
        {f.buttons.length < 3 ? (
          <button type="button" className="btn small ghost" onClick={() => setF({ ...f, buttons: [...f.buttons, { label: {}, route: "/home" }] })}>
            + {t("admin.notifications.addButton")}
          </button>
        ) : null}
      </div>
      <div className="push-preview">
        <div className="eyebrow">{t("admin.notifications.preview")}</div>
        <div className="push">
          <strong>{f.title.de || "…"}</strong>
          <div>{f.body.de || "…"}</div>
        </div>
      </div>
      <div className="actions">
        <button type="submit" className="btn" disabled={busy}>
          {t("admin.common.save")}
        </button>
        <button type="button" className="btn ghost" onClick={onClose}>
          {t("admin.common.cancel")}
        </button>
        <Msg msg={flash} />
        {template?.isCustom ? (
          <span style={{ marginInlineStart: "auto" }}>
            <ConfirmDelete label={t("admin.notifications.deleteTemplate")} onConfirm={() => void remove()} />
          </span>
        ) : null}
      </div>
    </form>
  );
}
